import { useState, useMemo, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, MoreVertical, Trash2, Edit, Archive, Eye, EyeOff, Settings, Calendar, BarChart3, Map, FileText, Users, Clock, Zap, LayoutDashboard, Table as TableIcon, Columns, ChevronDown, ChevronRight, History, GitBranch, Share2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { TableView } from '@/components/TaskManager/TableView';
import { KanbanView } from '@/components/TaskManager/KanbanView';
import { TimelineView } from '@/components/TaskManager/TimelineView';
import { CalendarView } from '@/components/TaskManager/CalendarView';
import { ChartView } from '@/components/TaskManager/ChartView';
import { MapView } from '@/components/TaskManager/MapView';
import { FormView } from '@/components/TaskManager/FormView';
import { WorkloadView } from '@/components/TaskManager/WorkloadView';
import { FilesView } from '@/components/TaskManager/FilesView';
import { NotificationBell } from '@/components/taskmanager/NotificationBell';
import { ChangeHistoryPanel } from '@/components/taskmanager/ChangeHistoryPanel';
import { BoardVersionsPanel } from '@/components/taskmanager/BoardVersionsPanel';
import { PublicShareDialog } from '@/components/taskmanager/PublicShareDialog';
import { useRealtimeCollaboration } from '@/hooks/useRealtimeCollaboration';
import { CellRenderer } from '@/components/taskmanager/CellRenderer';
import { UpdatesModal } from '@/components/taskmanager/UpdatesModal';

export default function TaskManager() {
  // State management
  const [selectedWorkspace, setSelectedWorkspace] = useState<number | null>(null);
  const [selectedBoard, setSelectedBoard] = useState<number | null>(null);
  const [selectedView, setSelectedView] = useState<number | null>(null);
  const [viewType, setViewType] = useState<'table' | 'kanban' | 'timeline' | 'calendar' | 'chart' | 'map' | 'form' | 'workload' | 'files'>('table');
  
  // Workspace dialogs
  const [isWorkspaceDialogOpen, setIsWorkspaceDialogOpen] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceDescription, setWorkspaceDescription] = useState('');
  
  // Board dialogs
  const [isBoardDialogOpen, setIsBoardDialogOpen] = useState(false);
  const [boardName, setBoardName] = useState('');
  const [boardDescription, setBoardDescription] = useState('');
  const [boardType, setBoardType] = useState<'main' | 'shareable' | 'private'>('main');
  
  // Group state
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());
  
  // Item state
  const [newItemNames, setNewItemNames] = useState<Record<number, string>>({});
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editingItemName, setEditingItemName] = useState('');
  
  // Column state
  const [isColumnDialogOpen, setIsColumnDialogOpen] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [newColumnTypeId, setNewColumnTypeId] = useState<number | null>(null);
  
  // Column value editing
  const [editingCell, setEditingCell] = useState<{ itemId: number; columnId: number } | null>(null);
  const [cellValue, setCellValue] = useState<any>('');
  
  // New panels and dialogs
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showVersionsPanel, setShowVersionsPanel] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  
  // Updates modal
  const [selectedItemForUpdates, setSelectedItemForUpdates] = useState<number | null>(null);
  const [isUpdatesModalOpen, setIsUpdatesModalOpen] = useState(false);
  
  // Get current user for realtime collaboration
  const { data: currentUser } = trpc.auth.me.useQuery();
  
  // Utils for query invalidation
  const utils = trpc.useUtils();
  
  // Setup realtime collaboration
  const realtime = useRealtimeCollaboration({
    boardId: selectedBoard || 0,
    user: currentUser || { id: 0, name: 'Guest', email: '', role: 'user' },
    onItemCreated: (item) => {
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
      toast.info('Novo item criado por outro usuário');
    },
    onItemUpdated: (item) => {
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
    },
    onItemDeleted: (itemId) => {
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
      toast.info('Item deletado por outro usuário');
    },
    onColumnValueChanged: (data) => {
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
    },
  });
  
  // Queries
  const { data: workspaces, refetch: refetchWorkspaces } = trpc.taskManager.workspaces.list.useQuery();
  const { data: boards, refetch: refetchBoards } = trpc.taskManager.boards.listByWorkspace.useQuery(
    { workspaceId: selectedWorkspace! },
    { enabled: !!selectedWorkspace }
  );
  const { data: groups, refetch: refetchGroups } = trpc.taskManager.groups.listByBoard.useQuery(
    { boardId: selectedBoard! },
    { enabled: !!selectedBoard }
  );
  const { data: items, refetch: refetchItems } = trpc.taskManager.items.listByBoard.useQuery(
    { boardId: selectedBoard! },
    { enabled: !!selectedBoard }
  );
  const { data: columns, refetch: refetchColumns } = trpc.taskManager.columns.listByBoard.useQuery(
    { boardId: selectedBoard! },
    { enabled: !!selectedBoard }
  );
  const { data: itemsWithValues, refetch: refetchItemsWithValues } = trpc.taskManager.items.listByBoardWithValues.useQuery(
    { boardId: selectedBoard! },
    { enabled: !!selectedBoard }
  );
  const { data: columnTypes } = trpc.taskManager.columns.listTypes.useQuery();
  const { data: views, refetch: refetchViews } = trpc.taskManager.views.listByBoard.useQuery(
    { boardId: selectedBoard! },
    { enabled: !!selectedBoard }
  );
  
  // Mutations - Workspaces
  const createWorkspaceMutation = trpc.taskManager.workspaces.create.useMutation({
    onSuccess: () => {
      toast.success('Workspace criado com sucesso!');
      utils.taskManager.workspaces.list.invalidate();
      setIsWorkspaceDialogOpen(false);
      setWorkspaceName('');
      setWorkspaceDescription('');
    },
  });
  
  const deleteWorkspaceMutation = trpc.taskManager.workspaces.delete.useMutation({
    onSuccess: () => {
      toast.success('Workspace excluído!');
      utils.taskManager.workspaces.list.invalidate();
      setSelectedWorkspace(null);
    },
  });
  
  // Mutations - Boards
  const createBoardMutation = trpc.taskManager.boards.create.useMutation({
    onSuccess: () => {
      toast.success('Board criado com sucesso!');
      utils.taskManager.boards.listByWorkspace.invalidate();
      setIsBoardDialogOpen(false);
      setBoardName('');
      setBoardDescription('');
    },
  });
  
  const deleteBoardMutation = trpc.taskManager.boards.delete.useMutation({
    onSuccess: () => {
      toast.success('Board excluído!');
      utils.taskManager.boards.listByWorkspace.invalidate();
      setSelectedBoard(null);
    },
  });
  
  const restoreVersionMutation = trpc.taskManager.boardVersions.restore.useMutation({
    onSuccess: () => {
      toast.success('Versão restaurada com sucesso!');
      utils.taskManager.groups.listByBoard.invalidate();
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
      utils.taskManager.columns.listByBoard.invalidate();
      setShowVersionsPanel(false);
    },
    onError: (error) => {
      toast.error('Erro ao restaurar versão');
      console.error(error);
    },
  });
  
  const archiveBoardMutation = trpc.taskManager.boards.archive.useMutation({
    onSuccess: () => {
      toast.success('Board arquivado!');
      utils.taskManager.boards.listByWorkspace.invalidate();
    },
  });
  
  // Mutations - Groups
  const createGroupMutation = trpc.taskManager.groups.create.useMutation({
    onSuccess: () => {
      toast.success('Grupo criado!');
      utils.taskManager.groups.listByBoard.invalidate();
      setNewGroupTitle('');
    },
  });
  
  const updateGroupMutation = trpc.taskManager.groups.update.useMutation({
    onSuccess: () => {
      utils.taskManager.groups.listByBoard.invalidate();
    },
  });
  
  const deleteGroupMutation = trpc.taskManager.groups.delete.useMutation({
    onSuccess: () => {
      toast.success('Grupo excluído!');
      utils.taskManager.groups.listByBoard.invalidate();
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
    },
  });
  
  // Mutations - Items
  const createItemMutation = trpc.taskManager.items.create.useMutation({
    onSuccess: () => {
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
    },
  });
  
  const updateItemMutation = trpc.taskManager.items.update.useMutation({
    onSuccess: () => {
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
      setEditingItemId(null);
    },
  });
  
  const deleteItemMutation = trpc.taskManager.items.delete.useMutation({
    onSuccess: () => {
      toast.success('Item excluído!');
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
    },
  });
  
  const moveItemMutation = trpc.taskManager.items.move.useMutation({
    onSuccess: () => {
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
    },
  });
  
  const duplicateItemMutation = trpc.taskManager.items.duplicate.useMutation({
    onSuccess: () => {
      toast.success('Item duplicado!');
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
    },
  });
  
  // Mutations - Columns
  const createColumnMutation = trpc.taskManager.columns.create.useMutation({
    onSuccess: () => {
      toast.success('Coluna criada!');
      utils.taskManager.columns.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
      setIsColumnDialogOpen(false);
      setNewColumnTitle('');
      setNewColumnTypeId(null);
    },
  });
  
  const deleteColumnMutation = trpc.taskManager.columns.delete.useMutation({
    onSuccess: () => {
      toast.success('Coluna excluída!');
      utils.taskManager.columns.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
    },
  });
  
  // Mutations - Column Values
  const setColumnValueMutation = trpc.taskManager.columnValues.set.useMutation({
    onSuccess: () => {
      utils.taskManager.items.listByBoardWithValues.invalidate();
      utils.taskManager.items.listByBoard.invalidate();
      setEditingCell(null);
      setCellValue('');
    },
  }); 
  // Mutations - Views
  const createViewMutation = trpc.taskManager.views.create.useMutation({
    onSuccess: () => {
      toast.success('View criada!');
      utils.taskManager.views.listByBoard.invalidate();
    },
  });
  
  // Handlers - Workspaces
  const handleCreateWorkspace = () => {
    if (!workspaceName.trim()) {
      toast.error('Nome do workspace é obrigatório');
      return;
    }
    createWorkspaceMutation.mutate({
      name: workspaceName,
      description: workspaceDescription,
    });
  };
  
  // Handlers - Boards
  const handleCreateBoard = () => {
    if (!selectedWorkspace) {
      toast.error('Selecione um workspace primeiro');
      return;
    }
    if (!boardName.trim()) {
      toast.error('Nome do board é obrigatório');
      return;
    }
    createBoardMutation.mutate({
      workspaceId: selectedWorkspace,
      name: boardName,
      description: boardDescription,
      type: boardType,
    });
  };
  
  // Handlers - Groups
  const handleCreateGroup = () => {
    if (!selectedBoard) return;
    if (!newGroupTitle.trim()) {
      toast.error('Título do grupo é obrigatório');
      return;
    }
    const position = groups?.length || 0;
    createGroupMutation.mutate({
      boardId: selectedBoard,
      title: newGroupTitle,
      position,
    });
  };
  
  const toggleGroupCollapse = (groupId: number) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupId)) {
      newCollapsed.delete(groupId);
    } else {
      newCollapsed.add(groupId);
    }
    setCollapsedGroups(newCollapsed);
  };
  
  // Handlers - Items
  const handleCreateItem = (groupId: number) => {
    if (!selectedBoard) return;
    const itemName = newItemNames[groupId];
    if (!itemName?.trim()) return;
    
    const groupItems = items?.filter(i => i.group_id === groupId) || [];
    const position = groupItems.length;
    
    createItemMutation.mutate({
      boardId: selectedBoard,
      groupId,
      name: itemName,
      position,
    });
    
    setNewItemNames({ ...newItemNames, [groupId]: '' });
  };
  
  const handleUpdateItem = () => {
    if (!editingItemId || !editingItemName.trim()) return;
    updateItemMutation.mutate({
      id: editingItemId,
      name: editingItemName,
    });
  };
  
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    
    const { source, destination, draggableId } = result;
    const itemId = parseInt(draggableId.replace('item-', ''));
    const targetGroupId = parseInt(destination.droppableId.replace('group-', ''));
    
    moveItemMutation.mutate({
      itemId,
      targetGroupId,
      targetPosition: destination.index,
    });
  };
  
  // Handlers - Columns
  const handleCreateColumn = () => {
    if (!selectedBoard || !newColumnTitle.trim() || !newColumnTypeId) {
      toast.error('Preencha todos os campos');
      return;
    }
    const position = columns?.length || 0;
    createColumnMutation.mutate({
      boardId: selectedBoard,
      columnTypeId: newColumnTypeId,
      title: newColumnTitle,
      position,
    });
  };
  
  // Handlers - Column Values
  const handleSaveCellValue = () => {
    if (!editingCell) return;
    setColumnValueMutation.mutate({
      itemId: editingCell.itemId,
      columnId: editingCell.columnId,
      value: cellValue,
    });
  };
  
  // Get items by group
  const getItemsByGroup = (groupId: number) => {
    return itemsWithValues?.filter(item => item.group_id === groupId) || [];
  };
  
  // Auto-select first workspace/board
  useEffect(() => {
    if (workspaces && workspaces.length > 0 && !selectedWorkspace) {
      setSelectedWorkspace(workspaces[0].id);
    }
  }, [workspaces, selectedWorkspace]);
  
  useEffect(() => {
    if (boards && boards.length > 0 && !selectedBoard) {
      setSelectedBoard(boards[0].id);
    }
  }, [boards, selectedBoard]);
  
  useEffect(() => {
    if (views && views.length > 0 && !selectedView) {
      const defaultView = views.find(v => v.is_default) || views[0];
      setSelectedView(defaultView.id);
      setViewType(defaultView.type);
    }
  }, [views, selectedView]);
  
  // Render Table View
  const renderTableView = () => {
    if (!groups || !items || !columns) return null;
    
    return (
      <div className="space-y-4">
        {/* Column Headers */}
        <div className="flex gap-2 border-b pb-2 overflow-x-auto">
          <div className="min-w-[300px] font-semibold border-r border-border">Item</div>
          {columns.map(col => (
            <div key={col.id} className="min-w-[150px] font-semibold flex items-center justify-between border-r border-border">
              {col.title}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteColumnMutation.mutate({ id: col.id })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsColumnDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-1" />
          Coluna
        </Button>
        {/* Groups and Items */}
        <DragDropContext onDragEnd={handleDragEnd}>
          {groups.map(group => (
            <div key={group.id} className="border rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleGroupCollapse(group.id)}
                  >
                    {collapsedGroups.has(group.id) ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                  <h3 className="font-semibold">{group.title}</h3>
                  <span className="text-sm text-muted-foreground">
                    ({getItemsByGroup(group.id).length} items)
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteGroupMutation.mutate({ id: group.id })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              {!collapsedGroups.has(group.id) && (
                <Droppable droppableId={`group-${group.id}`}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                      {getItemsByGroup(group.id).map((item, index) => (
                        <Draggable key={item.id} draggableId={`item-${item.id}`} index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="flex gap-2 items-center border-b pb-2"
                            >
                              <div className="min-w-[300px]">
                                {editingItemId === item.id ? (
                                  <div className="flex gap-2">
                                    <Input
                                      value={editingItemName}
                                      onChange={(e) => setEditingItemName(e.target.value)}
                                      onKeyDown={(e) => e.key === 'Enter' && handleUpdateItem()}
                                    />
                                    <Button size="sm" onClick={handleUpdateItem}>Salvar</Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingItemId(null)}>Cancelar</Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-between">
                                    <span>{item.name}</span>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setEditingItemId(item.id);
                                          setEditingItemName(item.name);
                                        }}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedItemForUpdates(item.id);
                                          setIsUpdatesModalOpen(true);
                                        }}
                                        title="Updates"
                                        className={item.updates_count > 0 ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}
                                      >
                                        <MessageSquare className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => duplicateItemMutation.mutate({ itemId: item.id })}
                                      >
                                        <Plus className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => deleteItemMutation.mutate({ id: item.id })}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              {columns.map(col => (
                                <div key={col.id} className="min-w-[150px] border-r border-border">
                                  <CellRenderer
                                    columnType={col.type_name || 'text'}
                                    value={(() => {
                                      // Special handling for read-only columns
                                      if (col.type_name === 'Item ID') {
                                        return item.id;
                                      }
                                      if (col.type_name === 'Creation Log') {
                                        const fullItem = itemsWithValues?.find(i => i.id === item.id);
                                        return fullItem ? {
                                          createdBy: fullItem.created_by_name || 'Unknown',
                                          createdAt: fullItem.created_at || Date.now()
                                        } : null;
                                      }
                                      // Default: get from columnValues
                                      return itemsWithValues ? (itemsWithValues.find(i => i.id === item.id)?.columnValues?.[col.id] || null) : null;
                                    })()}
                                    config={col.config ? JSON.parse(col.config) : {}}
                                    onChange={(newValue) => {
                                      setColumnValueMutation.mutate({
                                        itemId: item.id,
                                        columnId: col.id,
                                        value: newValue,
                                      });
                                    }}
                                    readOnly={false}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      
                      {/* New Item Input */}
                      <div className="flex gap-2 pt-2">
                        <Input
                          placeholder="+ Novo item"
                          value={newItemNames[group.id] || ''}
                          onChange={(e) => setNewItemNames({ ...newItemNames, [group.id]: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && handleCreateItem(group.id)}
                        />
                      </div>
                    </div>
                  )}
                </Droppable>
              )}
            </div>
          ))}
        </DragDropContext>
        
        {/* New Group */}
        <div className="flex gap-2">
          <Input
            placeholder="+ Novo grupo"
            value={newGroupTitle}
            onChange={(e) => setNewGroupTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
          />
          <Button onClick={handleCreateGroup}>Criar Grupo</Button>
        </div>
      </div>
    );
  };
  
  // Render Kanban View
  const renderKanbanView = () => {
    if (!groups || !items) return null;
    
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        <DragDropContext onDragEnd={handleDragEnd}>
          {groups.map(group => (
            <div key={group.id} className="min-w-[300px] border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">{group.title}</h3>
                <span className="text-sm text-muted-foreground">
                  {getItemsByGroup(group.id).length}
                </span>
              </div>
              
              <Droppable droppableId={`group-${group.id}`}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2 min-h-[200px]">
                    {getItemsByGroup(group.id).map((item, index) => (
                      <Draggable key={item.id} draggableId={`item-${item.id}`} index={index}>
                        {(provided) => (
                          <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between">
                                <span>{item.name}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteItemMutation.mutate({ id: item.id })}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    
                    <Input
                      placeholder="+ Novo item"
                      value={newItemNames[group.id] || ''}
                      onChange={(e) => setNewItemNames({ ...newItemNames, [group.id]: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateItem(group.id)}
                      className="mt-2"
                    />
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </DragDropContext>
        
        <div className="min-w-[300px]">
          <Input
            placeholder="+ Novo grupo"
            value={newGroupTitle}
            onChange={(e) => setNewGroupTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
          />
        </div>
      </div>
    );
  };
  
  // Render Timeline View (simplified)
  const renderTimelineView = () => {
    return (
      <div className="p-8 text-center">
        <Clock className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-semibold mb-2">Timeline View</h3>
        <p className="text-muted-foreground">Visualização de linha do tempo com dependências e marcos</p>
      </div>
    );
  };
  
  // Render Calendar View (simplified)
  const renderCalendarView = () => {
    return (
      <div className="p-8 text-center">
        <Calendar className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-semibold mb-2">Calendar View</h3>
        <p className="text-muted-foreground">Visualização de calendário com eventos e prazos</p>
      </div>
    );
  };
  
  // Render Chart View (simplified)
  const renderChartView = () => {
    return (
      <div className="p-8 text-center">
        <BarChart3 className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-semibold mb-2">Chart View</h3>
        <p className="text-muted-foreground">Gráficos e visualizações de dados</p>
      </div>
    );
  };
  
  // Render Map View (simplified)
  const renderMapView = () => {
    return (
      <div className="p-8 text-center">
        <Map className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-semibold mb-2">Map View</h3>
        <p className="text-muted-foreground">Visualização de mapa com localização de itens</p>
      </div>
    );
  };
  
  // Render Form View (simplified)
  const renderFormView = () => {
    return (
      <div className="p-8 text-center">
        <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-semibold mb-2">Form View</h3>
        <p className="text-muted-foreground">Formulário para coleta de dados</p>
      </div>
    );
  };
  
  // Render Workload View (simplified)
  const renderWorkloadView = () => {
    return (
      <div className="p-8 text-center">
        <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-semibold mb-2">Workload View</h3>
        <p className="text-muted-foreground">Visualização de carga de trabalho por pessoa</p>
      </div>
    );
  };
  
  // Render Files View (simplified)
  const renderFilesView = () => {
    return (
      <div className="p-8 text-center">
        <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-semibold mb-2">Files View</h3>
        <p className="text-muted-foreground">Galeria de arquivos anexados</p>
      </div>
    );
  };
  
  // Render current view
  const renderCurrentView = () => {
    switch (viewType) {
      case 'table': return renderTableView();
      case 'kanban': return renderKanbanView();
      case 'timeline': return renderTimelineView();
      case 'calendar': return renderCalendarView();
      case 'chart': return renderChartView();
      case 'map': return renderMapView();
      case 'form': return renderFormView();
      case 'workload': return renderWorkloadView();
      case 'files': return renderFilesView();
      default: return renderTableView();
    }
  };
  
  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Task Manager</h1>
        <div className="flex gap-2">
          <Dialog open={isWorkspaceDialogOpen} onOpenChange={setIsWorkspaceDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Novo Workspace
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Workspace</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  placeholder="Nome do workspace"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                />
                <Input
                  placeholder="Descrição (opcional)"
                  value={workspaceDescription}
                  onChange={(e) => setWorkspaceDescription(e.target.value)}
                />
                <Button onClick={handleCreateWorkspace} className="w-full">
                  Criar
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {/* Workspace Selector */}
      <div className="flex gap-4 items-center">
        <Select value={selectedWorkspace?.toString()} onValueChange={(v) => setSelectedWorkspace(parseInt(v))}>
          <SelectTrigger className="w-[300px]">
            <SelectValue placeholder="Selecione um workspace" />
          </SelectTrigger>
          <SelectContent>
            {workspaces?.map(ws => (
              <SelectItem key={ws.id} value={ws.id.toString()}>
                {ws.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {selectedWorkspace && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => deleteWorkspaceMutation.mutate({ id: selectedWorkspace })}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      {/* Board Selector */}
      {selectedWorkspace && (
        <div className="flex gap-4 items-center">
          <Select value={selectedBoard?.toString()} onValueChange={(v) => setSelectedBoard(parseInt(v))}>
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Selecione um board" />
            </SelectTrigger>
            <SelectContent>
              {boards?.map(board => (
                <SelectItem key={board.id} value={board.id.toString()}>
                  {board.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Dialog open={isBoardDialogOpen} onOpenChange={setIsBoardDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Novo Board
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Board</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  placeholder="Nome do board"
                  value={boardName}
                  onChange={(e) => setBoardName(e.target.value)}
                />
                <Input
                  placeholder="Descrição (opcional)"
                  value={boardDescription}
                  onChange={(e) => setBoardDescription(e.target.value)}
                />
                <Select value={boardType} onValueChange={(v: any) => setBoardType(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="main">Principal</SelectItem>
                    <SelectItem value="shareable">Compartilhável</SelectItem>
                    <SelectItem value="private">Privado</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleCreateBoard} className="w-full">
                  Criar
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {selectedBoard && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => archiveBoardMutation.mutate({ id: selectedBoard })}
              >
                <Archive className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => deleteBoardMutation.mutate({ id: selectedBoard })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHistoryPanel(!showHistoryPanel)}
                title="Histórico de Alterações"
              >
                <History className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowVersionsPanel(!showVersionsPanel)}
                title="Versões do Board"
              >
                <GitBranch className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowShareDialog(true)}
                title="Compartilhamento Público"
              >
                <Share2 className="h-4 w-4" />
              </Button>
              <NotificationBell />
            </>
          )}
        </div>
      )}
      
      {/* View Selector */}
      {selectedBoard && (
        <div className="flex gap-2 border-b pb-2">
          <Button
            variant={viewType === 'table' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('table')}
          >
            <TableIcon className="h-4 w-4 mr-2" />
            Table
          </Button>
          <Button
            variant={viewType === 'kanban' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('kanban')}
          >
            <Columns className="h-4 w-4 mr-2" />
            Kanban
          </Button>
          <Button
            variant={viewType === 'timeline' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('timeline')}
          >
            <Clock className="h-4 w-4 mr-2" />
            Timeline
          </Button>
          <Button
            variant={viewType === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('calendar')}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Calendar
          </Button>
          <Button
            variant={viewType === 'chart' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('chart')}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Chart
          </Button>
          <Button
            variant={viewType === 'map' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('map')}
          >
            <Map className="h-4 w-4 mr-2" />
            Map
          </Button>
          <Button
            variant={viewType === 'form' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('form')}
          >
            <FileText className="h-4 w-4 mr-2" />
            Form
          </Button>
          <Button
            variant={viewType === 'workload' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('workload')}
          >
            <Users className="h-4 w-4 mr-2" />
            Workload
          </Button>
          <Button
            variant={viewType === 'files' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('files')}
          >
            <FileText className="h-4 w-4 mr-2" />
            Files
          </Button>
        </div>
      )}
      
      {/* Main Content */}
      {selectedBoard ? (
        renderCurrentView()
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          Selecione ou crie um workspace e board para começar
        </div>
      )}
      
      {/* Side Panels */}
      {selectedBoard && showHistoryPanel && (
        <div className="fixed right-0 top-0 h-full w-96 bg-background border-l shadow-lg z-50">
          <ChangeHistoryPanel boardId={selectedBoard} />
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2"
            onClick={() => setShowHistoryPanel(false)}
          >
            ×
          </Button>
        </div>
      )}
      
      {selectedBoard && showVersionsPanel && (
        <div className="fixed right-0 top-0 h-full w-96 bg-background border-l shadow-lg z-50">
          <BoardVersionsPanel
            boardId={selectedBoard}
            onRestore={(versionId) => {
              restoreVersionMutation.mutate({ versionId });
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2"
            onClick={() => setShowVersionsPanel(false)}
          >
            ×
          </Button>
        </div>
      )}
      
      {selectedBoard && (
        <PublicShareDialog
          boardId={selectedBoard}
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
        />
      )}
      
      {/* Column Dialog */}
      <Dialog open={isColumnDialogOpen} onOpenChange={setIsColumnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Coluna</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Título da coluna"
              value={newColumnTitle}
              onChange={(e) => setNewColumnTitle(e.target.value)}
            />
            <Select value={newColumnTypeId?.toString()} onValueChange={(v) => setNewColumnTypeId(parseInt(v))}>
              <SelectTrigger>
                <SelectValue placeholder="Tipo de coluna" />
              </SelectTrigger>
              <SelectContent>
                {columnTypes?.map(type => (
                  <SelectItem key={type.id} value={type.id.toString()}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleCreateColumn} className="w-full">
              Criar Coluna
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Updates Modal */}
      <UpdatesModal
        itemId={selectedItemForUpdates}
        itemName={items?.find(i => i.id === selectedItemForUpdates)?.name || ''}
        isOpen={isUpdatesModalOpen}
        onClose={() => {
          setIsUpdatesModalOpen(false);
          setSelectedItemForUpdates(null);
        }}
      />
    </div>
  );
}
